import { captureException, withScope } from '@sentry/nextjs'

/**
 * Where in the checkout flow the failure happened. Surfaced as the `stage`
 * tag in Sentry so failures can be filtered / grouped by cause.
 *
 * - payment_method: Stripe / PayPal / GoCardless failed to produce a token
 *                   before we ever called our API (card declined, incomplete
 *                   card, PayPal SDK error, etc.)
 * - checkout:       POST checkout/ returned a non-2xx
 * - 3ds:            Stripe 3D Secure confirmation did not produce a setup intent
 * - confirm:        POST checkout/confirm returned a non-2xx
 * - runtime:        anything else thrown in the submit promise chain
 *                   (network failure, unexpected exception)
 */
export type PaymentErrorStage =
  | 'payment_method'
  | 'checkout'
  | '3ds'
  | 'confirm'
  | 'runtime'

type PaymentErrorContext = {
  /** Customer email, attached as the Sentry user so support can match reports */
  email?: string
  payment_type?: string
  status?: number
  body?: string
  [key: string]: unknown
}

const toError = (error: unknown): Error => {
  if (error instanceof Error) return error
  if (typeof error === 'string') return new Error(error)
  try {
    return new Error(JSON.stringify(error))
  } catch {
    return new Error(String(error))
  }
}

/**
 * Reports a checkout / payment failure to Sentry with enough context to
 * diagnose it. Never throws.
 *
 * Every failure in the payment step currently collapses into the generic
 * "There was a problem validating your payment method" message, so this is
 * the only place the real cause is recorded. Card details never pass through
 * here: Stripe only ever hands us an error message, never PAN / CVC.
 */
export const reportPaymentError = (
  stage: PaymentErrorStage,
  error: unknown,
  context: PaymentErrorContext = {},
) => {
  try {
    const err = toError(error)
    const { body, email, payment_type, status, ...extra } = context

    withScope((scope) => {
      if (email) scope.setUser({ email })
      scope.setTag('area', 'checkout')
      scope.setTag('stage', stage)
      if (payment_type) scope.setTag('payment_type', payment_type)
      if (typeof status === 'number')
        scope.setTag('http_status', String(status))

      // 4xx from our API is usually a validation / decline the customer can
      // act on; 5xx / network / 3DS are the ones we need to chase.
      scope.setLevel(
        typeof status === 'number' && status < 500 ? 'warning' : 'error',
      )

      // Response bodies can be large HTML error pages from the gateway.
      if (body !== undefined) scope.setExtra('body', body.slice(0, 4000))
      scope.setExtras(extra)

      // Group by stage + message rather than by the (identical) stack trace
      // every synthetic Error from this helper would share.
      scope.setFingerprint(['checkout', stage, err.message])

      captureException(err)
    })
  } catch (e) {
    // Reporting must never break checkout.
    console.error('reportPaymentError failed', e)
  }
}
